import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { parseError } from '@/lib/errors'
import { type SequenceStepInput } from '@/models/sequence'
import { type SequenceTemplate, useUpdateSequenceTemplate } from '@/models/sequenceTemplate'
import { SequenceStepsBuilder } from '../SalesLeads/Sequence/SequenceStepsBuilder'
import { DeleteSequenceTemplateButton } from './DeleteSequenceTemplateButton'
import { SequenceTemplateScopeNote } from './SequenceTemplateScopeNote'

interface SequenceTemplateEditorProps {
  template: SequenceTemplate | null
}

// Keyed by template id: a remount re-reads state and reloads the step builder.
export function SequenceTemplateEditor({ template }: SequenceTemplateEditorProps) {
  const [name, setName] = useState(() => template?.name ?? '')
  const [steps, setSteps] = useState<SequenceStepInput[]>(() => template?.steps ?? [])
  const [stepsChanged, setStepsChanged] = useState(false)
  // Template actions hide mid-step, so the step's own submit is the only way on.
  const [stepEditorOpen, setStepEditorOpen] = useState(false)

  const updateMutation = useUpdateSequenceTemplate()

  if (!template) {
    return (
      <div className="text-muted-foreground flex h-full flex-col items-center justify-center gap-2 text-sm">
        <p>Templates save a list of steps to start a sequence from.</p>
        <p>Create one with New template.</p>
      </div>
    )
  }

  const { id } = template
  const dirty = stepsChanged || name !== template.name

  function handleStepsChange(next: SequenceStepInput[]) {
    setSteps(next)
    setStepsChanged(true)
  }

  function handleSave() {
    updateMutation.mutate(
      { id, name, steps },
      {
        onSuccess: () => setStepsChanged(false),
        onError: (err) => toast.error(parseError(err)),
      }
    )
  }

  return (
    <div className="flex flex-col gap-3">
      <SequenceTemplateScopeNote template={template} />
      <Input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Template name"
        aria-label="Template name"
        className="text-xl font-semibold"
      />
      <div className="flex flex-col gap-1">
        <p className="text-sm font-medium">Steps</p>
        <SequenceStepsBuilder
          initialSteps={template.steps}
          onChange={handleStepsChange}
          emptyText="No steps yet."
          onEditingChange={setStepEditorOpen}
        />
      </div>
      {!stepEditorOpen && (
        <div className="flex items-center justify-between gap-2 border-t pt-3">
          <DeleteSequenceTemplateButton template={template} />
          <Button
            onClick={handleSave}
            disabled={!dirty || !name.trim()}
            loading={updateMutation.isPending ? 'Saving…' : undefined}
          >
            Save
          </Button>
        </div>
      )}
    </div>
  )
}
